import LandingStatBox from "@/components/landing/LandingStatBox";

/**
 * Landing hero — asymmetric split:
 *   Left  → overline + oversized serif headline + supporting copy
 *   Right → clinical "catalog compound preview" card
 *
 * Palette mapping from the reference (teal/mint) → BBK bone/charcoal/oxblood:
 *   accent teal    → oxblood
 *   mustard chip   → sand
 *   background     → bone with soft warm radial gradient
 */
export default function LandingHero() {
  return (
    <section
      className="relative overflow-hidden bg-bone"
      data-testid="landing-hero"
    >
      {/* Ambient background — soft warm radial + faint grain, very subtle */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background:
            "radial-gradient(1200px 500px at 85% -10%, rgba(140,123,102,0.10), transparent 60%), radial-gradient(900px 500px at -10% 30%, rgba(74,14,23,0.05), transparent 65%)",
        }}
      />
      {/* Tiny dot dust — clinical texture without noise */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        aria-hidden
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(26,26,26,0.10) 1px, transparent 0)",
          backgroundSize: "42px 42px",
          maskImage:
            "radial-gradient(1000px 500px at 30% 40%, black, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(1000px 500px at 30% 40%, black, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-14 sm:pt-20 lg:pt-24 pb-16 sm:pb-24 lg:pb-28">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 xl:gap-24 items-center">
          {/* Left column */}
          <div className="animate-fade-up">
            <p
              className="text-[13px] uppercase tracking-[0.28em] text-oxblood font-medium"
              data-testid="landing-hero-eyebrow"
            >
              Wholesale · Research Grade · Members Only
            </p>

            <h1
              className="mt-6 sm:mt-8 font-serif text-charcoal tracking-tight leading-[0.95] text-[36px] sm:text-5xl lg:text-6xl xl:text-[64px]"
              data-testid="landing-hero-heading"
            >
              You're serious
              <br />
              about your research.
              <br />
              <span className="italic text-oxblood">So are we.</span>
            </h1>

            <p className="mt-8 max-w-[38ch] text-base sm:text-lg text-stone leading-relaxed">
              A members-only supply channel for research laboratories. Priced at kit quantities. Ships from a US warehouse.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <a
                href="#request-access"
                className="group inline-flex items-center gap-2 rounded-full bg-charcoal text-bone pl-5 pr-3 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-oxblood transition-colors"
                data-testid="landing-hero-primary-cta"
              >
                Gain access to the research catalog
                <span
                  className="grid place-items-center w-7 h-7 rounded-full bg-bone/15 group-hover:bg-bone/25 transition-colors"
                  aria-hidden
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </a>

              <a
                href="/enter"
                className="text-[13px] uppercase tracking-[0.24em] text-charcoal border-b border-charcoal/40 hover:border-oxblood hover:text-oxblood pb-1 transition-colors"
                data-testid="landing-hero-secondary-cta"
              >
                Already a researcher? Sign in
              </a>
            </div>
          </div>

          {/* Right column — rotating stat panel */}
          <div className="animate-fade-up [animation-delay:120ms]">
            <LandingStatBox />
          </div>
        </div>
      </div>

      {/* Bottom hairline that dovetails into the next section */}
      <div className="hairline max-w-[1400px] mx-auto" />
    </section>
  );
}
